import { useState } from "react";
import { Plus, Trash, ArrowUp, ArrowDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function PerguntasProduto({ perguntasProduto, setPerguntasProduto }) {
    const [novaPergunta, setNovaPergunta] = useState("");

    // --- CRUD Perguntas ---
    const adicionarPergunta = () => {
        if (!novaPergunta.trim()) return;
        const pergunta = {
            id: Date.now(),
            texto: novaPergunta,
            obrigatoria: false,
            opcoes: [],
            novaOpcaoTexto: "",
            novaOpcaoValor: "",
            ordem: perguntasProduto.length // ordem baseada no índice atual
        };
        setPerguntasProduto([...perguntasProduto, pergunta]);
        setNovaPergunta("");
    };

    const atualizarTextoPergunta = (id, texto) => {
        setPerguntasProduto(
            perguntasProduto.map(p => p.id === id ? { ...p, texto } : p)
        );
    };

    const moverPergunta = (index, direcao) => {
        const novaLista = [...perguntasProduto];
        const novoIndex = index + direcao;
        if (novoIndex < 0 || novoIndex >= novaLista.length) return;
        [novaLista[index], novaLista[novoIndex]] = [novaLista[novoIndex], novaLista[index]];

        novaLista.forEach((p, i) => p.ordem = i);

        setPerguntasProduto(novaLista);
    };

    const removerPergunta = (id) => {
        setPerguntasProduto(perguntasProduto.filter(p => p.id !== id));
    };

    const toggleObrigatoria = (id) => {
        setPerguntasProduto(
            perguntasProduto.map(p =>
                p.id === id ? { ...p, obrigatoria: !p.obrigatoria } : p
            )
        );
    };

    // --- CRUD Opções ---
    const adicionarOpcao = (perguntaId) => {
        setPerguntasProduto(
            perguntasProduto.map(p =>
                p.id === perguntaId
                    ? {
                        ...p,
                        opcoes: [
                            ...p.opcoes,
                            {
                                id: Date.now(),
                                texto: p.novaOpcaoTexto,
                                valorExtra: parseFloat(p.novaOpcaoValor) || 0,
                                ordem: p.opcoes.length // ordem da opção
                            }
                        ],
                        novaOpcaoTexto: "",
                        novaOpcaoValor: ""
                    }
                    : p
            )
        );
    };

    const atualizarOpcao = (perguntaId, opcaoId, campo, valor) => {
        setPerguntasProduto(
            perguntasProduto.map(p =>
                p.id === perguntaId
                    ? {
                        ...p,
                        opcoes: p.opcoes.map(o =>
                            o.id === opcaoId
                                ? {
                                    ...o,
                                    [campo]: campo === "valorExtra"
                                        ? parseFloat(valor.replace(",", ".")) || 0
                                        : valor
                                }
                                : o
                        )
                    }
                    : p
            )
        );
    };

    const moverOpcao = (perguntaId, index, direcao) => {
        setPerguntasProduto(
            perguntasProduto.map(p =>
                p.id === perguntaId
                    ? {
                        ...p,
                        opcoes: (() => {
                            const novaLista = [...p.opcoes];
                            const novoIndex = index + direcao;
                            if (novoIndex < 0 || novoIndex >= novaLista.length) return novaLista;
                            [novaLista[index], novaLista[novoIndex]] = [novaLista[novoIndex], novaLista[index]];

                            novaLista.forEach((o, i) => o.ordem = i);

                            return novaLista;
                        })()
                    }
                    : p
            )
        );
    };

    const removerOpcao = (perguntaId, opcaoId) => {
        setPerguntasProduto(
            perguntasProduto.map(p =>
                p.id === perguntaId
                    ? { ...p, opcoes: p.opcoes.filter(o => o.id !== opcaoId) }
                    : p
            )
        );
    };

    const atualizarCampoNovaOpcao = (perguntaId, campo, valor) => {
        setPerguntasProduto(
            perguntasProduto.map(p =>
                p.id === perguntaId
                    ? { ...p, [campo]: valor }
                    : p
            )
        );
    };

    return (
        <div className="space-y-3 mt-2">
            <AnimatePresence>
                {perguntasProduto.map((pergunta, i) => (
                    <motion.div
                        key={pergunta.id}
                        layout
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.3, type: "spring" }}
                        className="border p-3 rounded-lg bg-gray-50 shadow-sm"
                    >
                        {/* Cabeçalho da pergunta */}
                        <div className="flex justify-between items-center">
                            <input
                                type="text"
                                className="font-medium bg-transparent border-b border-dashed border-gray-300 focus:outline-none focus:border-blue-500"
                                value={pergunta.texto}
                                onChange={(e) => atualizarTextoPergunta(pergunta.id, e.target.value)}
                            />

                            <div className="flex space-x-2 items-center">
                                <label className="text-sm">
                                    <input
                                        type="checkbox"
                                        checked={pergunta.obrigatoria}
                                        onChange={() => toggleObrigatoria(pergunta.id)}
                                    />{" "}
                                    Obrigatória
                                </label>
                                <button onClick={() => moverPergunta(i, -1)}>
                                    <ArrowUp className="w-4 h-4 text-gray-500 hover:text-black" />
                                </button>
                                <button onClick={() => moverPergunta(i, 1)}>
                                    <ArrowDown className="w-4 h-4 text-gray-500 hover:text-black" />
                                </button>
                                <button onClick={() => removerPergunta(pergunta.id)}>
                                    <Trash className="w-4 h-4 text-red-500" />
                                </button>
                            </div>
                        </div>

                        {/* Lista de opções */}
                        <div className="mt-2 space-y-1">
                            {pergunta.opcoes.map((opcao, j) => (
                                <motion.div
                                    key={opcao.id}
                                    layout
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: 20 }}
                                    transition={{ duration: 0.25, type: "spring" }}
                                    className="flex justify-between items-center bg-white border rounded px-2 py-1"
                                >
                                    <input
                                        type="text"
                                        className="bg-transparent flex-1 focus:outline-none"
                                        value={opcao.texto}
                                        onChange={(e) =>
                                            atualizarOpcao(pergunta.id, opcao.id, "texto", e.target.value)
                                        }
                                    />
                                    <div className="flex items-center space-x-1">
                                        <span className="text-gray-500">R$</span>
                                        <input
                                            type="number"
                                            className="w-20 text-right bg-transparent focus:outline-none border-b border-dashed border-gray-300"
                                            placeholder="0,00"
                                            value={opcao.valorExtra || ""}
                                            onChange={(e) =>
                                                atualizarOpcao(pergunta.id, opcao.id, "valorExtra", e.target.value)
                                            }
                                        />
                                    </div>
                                    <div className="flex space-x-1">
                                        <button onClick={() => moverOpcao(pergunta.id, j, -1)}>
                                            <ArrowUp className="w-4 h-4 text-gray-500 hover:text-black" />
                                        </button>
                                        <button onClick={() => moverOpcao(pergunta.id, j, 1)}>
                                            <ArrowDown className="w-4 h-4 text-gray-500 hover:text-black" />
                                        </button>
                                        <button
                                            onClick={() => removerOpcao(pergunta.id, opcao.id)}
                                            className="text-red-500"
                                        >
                                            <Trash className="w-4 h-4" />
                                        </button>
                                    </div>
                                </motion.div>
                            ))}
                        </div>

                        {/* Adicionar opção */}
                        <div className="flex mt-2 space-x-2">
                            <input
                                type="text"
                                placeholder="Texto da opção"
                                className="border px-2 py-1 rounded w-full"
                                value={pergunta.novaOpcaoTexto}
                                onChange={(e) => atualizarCampoNovaOpcao(pergunta.id, "novaOpcaoTexto", e.target.value)}
                            />
                            <input
                                type="number"
                                placeholder="Valor extra"
                                className="border px-2 py-1 rounded w-28"
                                value={pergunta.novaOpcaoValor}
                                onChange={(e) => atualizarCampoNovaOpcao(pergunta.id, "novaOpcaoValor", e.target.value)}
                            />
                            <button
                                onClick={() => adicionarOpcao(pergunta.id)}
                                className="bg-blue-500 text-white px-3 rounded"
                            >
                                <Plus className="w-4 h-4" />
                            </button>
                        </div>
                    </motion.div>
                ))}
            </AnimatePresence>

            {/* Criar nova pergunta */}
            <div className="flex space-x-2">
                <input
                    type="text"
                    placeholder="Digite a pergunta..."
                    className="border px-3 py-2 rounded w-full"
                    value={novaPergunta}
                    onChange={(e) => setNovaPergunta(e.target.value)}
                />
                <button
                    onClick={adicionarPergunta}
                    className="bg-green-600 text-white px-4 py-2 rounded"
                >
                    Adicionar
                </button>
            </div>
        </div>
    );
}
